/**
 * Qinggong in the bout (lane QINGGONG-POSES, duel v3 P1.5): the body of each duel move, so a step back, a step to
 * either side and a step in each read as their own move instead of the open world's dash. Research:
 * docs/research/QINGGONG_POSES.md. Picked by render/duelMoveAnim.ts from the rules' step kind (systems/duelMove.ts).
 *
 * Each move is one motion over the step and its punishable stop (the rules' `DUEL_MOVE.stop`), 9 keys from the ready
 * stance back to it, blended on the Catmull-Rom spline (motions/spline.ts). The keys are evenly spaced in time, so the
 * landing key falls where the step's travel ends and the rest is the stop: the sink, then the rise into the stance.
 *
 * Every key is laid over the standing pose as a stance key (the idle library's `IDLE_KEYS`, rig/skeleton.ts `Stance`):
 * that is what gives the rig a weight over one leg (the hips shifted over it, the free hip and the other shoulder
 * dropping), a turn of the shoulders and the feet apart — a side step lives in those, since the rig's own numbers only
 * move the body forward and back.
 */
import type { Stance } from '../rig/skeleton';
import { IDLE_KEYS } from './idles';
import type { Ease, FarArm, MotionDef, NearArm, PoseRig, Rig } from './kit';
import type { Pose } from './index';

/** One key of a move. */
interface Key {
  /** The weight: +1 all over the right leg (A, the weapon side), −1 the left (B); its size is the hips' shift. */
  w?: number;
  /** Feet apart (as `Bearing.stance`; head-on and three-quarter on). */
  feet: number;
  /** Forward offset of the back (B) and front (A) foot, turned views. */
  f: [number, number];
  /** Lift of the back (B) and front (A) foot. */
  l?: [number, number];
  o: number;
  lean: number;
  tilt: number;
  /** The shoulders turned about the spine (positive: the right shoulder forward). */
  turn?: number;
  hipDrop?: number;
  shoulderDrop?: number;
  sway?: number;
  hem?: number;
  flare?: number;
  headDx?: number;
  arm: NearArm;
  far: FarArm;
  /** The off hand's own place (classic design coordinates, as `POSE_HANDS`), over its arm shape's. */
  B?: [number, number];
  face?: Rig['face'];
}

/** The sword-fingers flung up and back past the shoulder for balance (not over the head: from behind, the arm crossed it). */
const FLUNG: [number, number] = [-13, 17];

/** The same key for a step to the other side: the weight and the lifted foot on the other leg. */
const mirror = (k: Key): Key => ({
  ...k,
  ...(k.w !== undefined ? { w: -k.w } : {}),
  ...(k.l ? { l: [k.l[1], k.l[0]] as [number, number] } : {}),
  ...(k.turn !== undefined ? { turn: -k.turn } : {}),
});

/** ก้าวหลบหลัง (撤步 / 後躍): a crouch, a push off the front foot, a low hop back with the blade kept levelled at the
 * foe and the sword-fingers flung up, landing on the back foot into an empty stance (虛步: the weight sunk over the
 * back leg, the front foot light), then up into the stance. */
const BACK: Key[] = [
  { feet: 1.2, f: [-6, 6], o: 6, lean: 1, tilt: 10, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { feet: 1.3, f: [-12, 7], l: [4, 0], o: 2, lean: -2, tilt: -6, sway: -1.5, hem: 2, flare: 1, arm: 'readyBlade', far: 'up', B: FLUNG, face: 'block' },
  { feet: 1.2, f: [-8, 4], l: [5, 7], o: -1, lean: -2, tilt: -10, sway: -2.5, hem: 3, flare: 3, arm: 'readyBlade', far: 'up', B: FLUNG, face: 'block' },
  { feet: 1.4, f: [-11, 6], l: [0, 3], o: 6, lean: -2, tilt: -2, sway: -2, hem: 2, flare: 1, arm: 'readyBlade', far: 'hang', face: 'block' },
  { w: -0.8, feet: 1.4, f: [-9, 7], o: 9, lean: -1, tilt: 6, hipDrop: 1, shoulderDrop: 0.6, sway: -0.5, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { w: -0.4, feet: 1.25, f: [-7, 6], o: 6, lean: 0, tilt: 5, sway: 0.3, arm: 'readyBlade', far: 'fwd' },
  { feet: 1.15, f: [-6, 5], o: 4, lean: 1, tilt: 5, arm: 'readyBlade', far: 'fwd' },
];

/** ก้าวหลบข้าง (閃步) to the right: the weight loaded on the trail leg, the lead foot opens out and the body slides
 * low over it, the lead shoulder dropped into the move and the sword-fingers thrown up for balance; it lands deep on the
 * lead leg and the trail foot is drawn in after it. The left is its mirror. */
const SIDE: Key[] = [
  { w: -1.2, feet: 1.4, f: [-6, 5], o: 6, lean: 1, tilt: 7, hipDrop: 1.2, shoulderDrop: 0.8, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { w: 1, feet: 2.1, f: [-6, 5], l: [0, 4], o: 3, lean: 1, tilt: 4, hipDrop: 1, shoulderDrop: 1.6, sway: 1, hem: 2, flare: 2, arm: 'readyBlade', far: 'up', B: FLUNG, face: 'block' },
  { w: 2.4, feet: 2.6, f: [-6, 5], l: [3, 2], o: 1, lean: 1, tilt: 3, hipDrop: 2, shoulderDrop: 2.6, sway: 1.6, hem: 3, flare: 3, arm: 'readyBlade', far: 'up', B: FLUNG, face: 'block' },
  { w: 3.4, feet: 2.8, f: [-6, 5], l: [3, 0], o: 9, lean: 1, tilt: 7, hipDrop: 2.5, shoulderDrop: 3, sway: 1, hem: 2, flare: 2, arm: 'readyBlade', far: 'up', B: FLUNG, face: 'block' },
  { w: 2.4, feet: 2, f: [-6, 5], l: [2, 0], o: 8, lean: 1, tilt: 7, hipDrop: 2, shoulderDrop: 1.8, sway: 0.4, hem: 1, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { w: 1.2, feet: 1.5, f: [-6, 5], o: 6, lean: 1, tilt: 6, hipDrop: 1, shoulderDrop: 0.8, arm: 'readyBlade', far: 'fwd' },
  { w: 0.4, feet: 1.25, f: [-6, 5], o: 4, lean: 1, tilt: 5, arm: 'readyBlade', far: 'fwd' },
];

/** ก้าวหลบหน้า (上步 / 進步): sunk and driven off the back foot, the front knee carried through, a long low glide with
 * the blade leading and the sword-fingers streaming back, planted in a bow stance (弓步), the back foot drawn up after
 * it (跟步), then the stance. The longest stop of the three: the body is committed forward. */
const FWD: Key[] = [
  { feet: 1.15, f: [-7, 5], o: 6, lean: 1, tilt: 12, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { feet: 1.1, f: [-12, 9], l: [3, 5], o: 2, lean: 3, tilt: 18, sway: 2, hem: 1, flare: 1, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1.1, f: [-14, 12], l: [4, 1], o: 3, lean: 3, tilt: 16, sway: 2.2, hem: 2, flare: 1, arm: 'readyBlade', far: 'back', face: 'block' },
  { w: 0.5, feet: 1.4, f: [-13, 11], o: 9, lean: 3, tilt: 12, hipDrop: 0.5, sway: 1.8, hem: 1, arm: 'readyBlade', far: 'back', face: 'block' },
  { w: 0.4, feet: 1.3, f: [-8, 9], l: [3, 0], o: 7, lean: 2, tilt: 10, sway: 1, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1.15, f: [-6, 6], o: 5, lean: 1, tilt: 7, sway: -0.8, arm: 'readyBlade', far: 'fwd' },
  { feet: 1.15, f: [-6, 5], o: 4, lean: 1, tilt: 6, sway: -0.3, arm: 'readyBlade', far: 'fwd' },
];

/** สปริงตัวกระโดด (bout take-off): sunk on both knees with the blade still on the foe, the sword-fingers swung down
 * and back, then driven up through the legs to the toes, the arm flung up, the knees drawn up as the feet leave. */
const TAKEOFF: Key[] = [
  { feet: 1.2, f: [-6, 5], o: 6, lean: 1, tilt: 10, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { feet: 1.2, f: [-5, 4], o: 9, lean: 1, tilt: 16, headDx: 1, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1.15, f: [-4, 4], o: 7, lean: 1, tilt: 12, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1.1, f: [-4, 3], o: 2, lean: 0, tilt: 4, arm: 'readyBlade', far: 'up', B: FLUNG, face: 'block' },
  { feet: 1, f: [-4, 2], l: [2, 1], o: -1, lean: 0, tilt: 0, sway: 0.5, arm: 'readyBlade', far: 'up', B: FLUNG },
  { feet: 1, f: [-5, 3], l: [4, 5], o: 0, lean: 0, tilt: 2, sway: 0.8, hem: 1, arm: 'readyBlade', far: 'up', B: FLUNG },
  { feet: 1, f: [-5, 4], l: [3, 8], o: 1, lean: 0, tilt: 5, sway: 1, hem: 1, arm: 'readyBlade', far: 'up', B: FLUNG },
];

/** The leap in the bout, by the fall (as the open world's arc, render/qinggongAnim.ts, but the blade kept on the foe):
 * rising with the front knee high, tucked at the top, falling with the robes blown up, the legs reaching for the ground. */
const AIR: Key[] = [
  { feet: 1, f: [-5, 4], l: [3, 9], o: 1, lean: 0, tilt: 6, sway: 0.8, hem: 1, arm: 'readyBlade', far: 'up', B: FLUNG },
  { feet: 1, f: [-4, 4], l: [5, 10], o: 2, lean: 0, tilt: 8, sway: 1.2, hem: 2, arm: 'readyBlade', far: 'up', B: FLUNG },
  { feet: 1, f: [-3, 3], l: [9, 11], o: 3, lean: 0, tilt: 12, sway: 1.8, hem: 3, flare: 2, arm: 'readyBlade', far: 'up', B: FLUNG },
  { feet: 1, f: [-3, 3], l: [9, 10], o: 3, lean: 0, tilt: 10, sway: 2, hem: 3, flare: 2, arm: 'readyBlade', far: 'up', B: FLUNG },
  { feet: 1.1, f: [-4, 4], l: [4, 5], o: 1, lean: 0, tilt: 2, sway: 2.6, hem: 3, flare: 3, arm: 'readyBlade', far: 'hang' },
  { feet: 1.1, f: [-4, 4], l: [3, 3], o: 0, lean: -1, tilt: -3, sway: 3, hem: 3, flare: 4, arm: 'readyBlade', far: 'back' },
  { feet: 1.15, f: [-5, 5], l: [2, 0], o: 0, lean: -1, tilt: -3, sway: 2, hem: 3, flare: 3, arm: 'readyBlade', far: 'back' },
  { feet: 1.15, f: [-6, 5], l: [1, 0], o: 1, lean: 0, tilt: 0, sway: 1.5, hem: 3, flare: 2, arm: 'readyBlade', far: 'back' },
];

/** พุ่งกลางอากาศ (air dash): coiled at the top, then laid out flat along the dart, the blade leading and the legs
 * streaming long behind, the sword-fingers back; drawn in again to the tuck. */
const AIR_DASH: Key[] = [
  { feet: 1, f: [-3, 3], l: [8, 9], o: 4, lean: 1, tilt: 16, sway: 1, hem: 2, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1, f: [-12, 6], l: [6, 4], o: 2, lean: 3, tilt: 30, sway: 3, hem: 3, flare: 2, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1, f: [-13, 7], l: [7, 4], o: 2, lean: 3, tilt: 32, sway: 3.2, hem: 3, flare: 2, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1, f: [-13, 7], l: [8, 5], o: 2, lean: 3, tilt: 31, sway: 3, hem: 3, flare: 2, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1, f: [-11, 6], l: [7, 6], o: 3, lean: 2, tilt: 26, sway: 2.6, hem: 3, flare: 2, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1, f: [-6, 4], l: [8, 8], o: 3, lean: 1, tilt: 16, sway: 2, hem: 3, flare: 1, arm: 'readyBlade', far: 'back' },
  { feet: 1, f: [-4, 3], l: [9, 10], o: 3, lean: 0, tilt: 12, sway: 2, hem: 3, flare: 2, arm: 'readyBlade', far: 'up', B: FLUNG },
];

/** ท่าโฉบ (dive strike), a hawk's swoop: the blade drawn back to the hip at the top, the body turned over it, then
 * plunging down head first behind the levelled point, the legs trailing up behind, the blade driven down through the
 * landing; ends on the impact. (A blade raised overhead crossed the face in profile and head-on, so it stays low.) */
const DIVE: Key[] = [
  { feet: 1, f: [-3, 3], l: [8, 9], o: 3, lean: 0, tilt: -4, sway: 1, hem: 3, arm: 'readyBlade', far: 'up', B: FLUNG, face: 'atk' },
  { feet: 1, f: [-4, 3], l: [6, 10], o: 2, lean: -1, tilt: -8, sway: 1.2, hem: 3, arm: 'thrustChamber', far: 'up', B: FLUNG, face: 'atk' },
  { feet: 1, f: [-6, 4], l: [8, 6], o: 2, lean: 1, tilt: 14, sway: 2, hem: 3, flare: 2, arm: 'thrustChamber', far: 'back', face: 'atk' },
  { feet: 1, f: [-10, 2], l: [12, 4], o: 3, lean: 2, tilt: 28, sway: 3, hem: 3, flare: 3, arm: 'thrust', far: 'back', face: 'atk' },
  { feet: 1, f: [-10, 3], l: [12, 3], o: 3, lean: 2, tilt: 30, sway: 3.2, hem: 3, flare: 3, arm: 'thrust', far: 'back', face: 'atk' },
  { feet: 1.1, f: [-8, 8], l: [6, 1], o: 4, lean: 2, tilt: 24, sway: 2.5, hem: 3, flare: 2, arm: 'cut', far: 'back', face: 'atk' },
  { feet: 1.3, f: [-10, 9], o: 10, lean: 2, tilt: 26, sway: 1, hem: 2, flare: 1, arm: 'cut', far: 'up', B: FLUNG, face: 'atk' },
];

/** The dive's landing: the blade driven down into the ground, held, then the body hauled up into the stance (played
 * over the blow's own time when it lands, over the longer punishable stop when it misses). */
const DIVE_LAND: Key[] = [
  { feet: 1.35, f: [-10, 9], o: 11, lean: 2, tilt: 28, sway: 0.5, hem: 1, arm: 'cut', far: 'up', B: FLUNG, face: 'atk' },
  { feet: 1.35, f: [-10, 9], o: 11, lean: 2, tilt: 27, sway: 0, hem: 1, arm: 'cut', far: 'up', B: FLUNG, face: 'atk' },
  { feet: 1.3, f: [-9, 8], o: 9, lean: 2, tilt: 20, arm: 'readyBlade', far: 'hang', face: 'block' },
  { feet: 1.25, f: [-8, 6], o: 7, lean: 1, tilt: 14, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1.2, f: [-7, 5], o: 5, lean: 1, tilt: 8, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { feet: 1.15, f: [-6, 5], o: 4, lean: 1, tilt: 6, arm: 'readyBlade', far: 'fwd' },
];

/** The landing stop: the feet take the ground, the body sinks deep through the knees (the punishable moment), then
 * rises into the stance. */
const LAND: Key[] = [
  { feet: 1.2, f: [-6, 5], o: 4, lean: 0, tilt: 4, sway: 1, hem: 3, flare: 2, arm: 'readyBlade', far: 'back' },
  { feet: 1.3, f: [-6, 5], o: 9, lean: 1, tilt: 14, sway: 0.5, hem: 2, flare: 1, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1.3, f: [-6, 5], o: 10, lean: 1, tilt: 16, headDx: 1, sway: 0, hem: 1, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1.25, f: [-6, 5], o: 8, lean: 1, tilt: 12, sway: -0.3, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { feet: 1.2, f: [-6, 5], o: 6, lean: 1, tilt: 8, arm: 'readyBlade', far: 'fwd' },
  { feet: 1.15, f: [-6, 5], o: 4, lean: 1, tilt: 6, arm: 'readyBlade', far: 'fwd' },
];

/** สไลด์ (the bout's ground slide): pushed off low into a skid on the back knee — the front leg long ahead, the back
 * knee folded down near the ground with the shin trailing behind (the drop stance's line, 仆步, turned into a skid; with
 * the back foot under the hips it read as sitting on a chair), the trunk a little forward into the slide, the
 * sword-fingers trailing low behind — the robes streaming back until the slide slows and they swing forward past it. */
const SLIDE_: Key[] = [
  { feet: 1.2, f: [-7, 8], o: 8, lean: 1, tilt: 10, arm: 'readyBlade', far: 'back', face: 'block' },
  { feet: 1.4, f: [-9, 12], o: 12, lean: 1, tilt: 6, sway: 2.5, flare: 1, arm: 'readyBlade', far: 'back', B: [-12, 40], face: 'block' },
  { feet: 1.4, f: [-9, 12], o: 12, lean: 1, tilt: 5, sway: 2.8, flare: 1, arm: 'readyBlade', far: 'back', B: [-13, 41], face: 'block' },
  { feet: 1.4, f: [-9, 12], o: 12, lean: 1, tilt: 5, sway: 2.2, flare: 1, arm: 'readyBlade', far: 'back', B: [-13, 41], face: 'block' },
  { feet: 1.4, f: [-9, 11], o: 12, lean: 1, tilt: 5, sway: 1.4, arm: 'readyBlade', far: 'back', B: [-12, 40], face: 'block' },
  { feet: 1.4, f: [-9, 11], o: 12, lean: 1, tilt: 6, sway: 0.6, arm: 'readyBlade', far: 'back', B: [-11, 40], face: 'block' },
  { feet: 1.4, f: [-9, 11], o: 12, lean: 1, tilt: 7, sway: -0.5, arm: 'readyBlade', far: 'back', B: [-10, 39], face: 'block' },
];

/** The stop after a slide that ran out: stuck low, then the weight rocked forward over the front foot and the body
 * pushed up out of the drop stance into the stance. */
const SLIDE_STOP: Key[] = [
  { feet: 1.4, f: [-9, 11], o: 12, lean: 1, tilt: 7, sway: -0.3, arm: 'readyBlade', far: 'back', B: [-10, 39], face: 'block' },
  { feet: 1.35, f: [-8, 9], o: 11, lean: 1, tilt: 10, arm: 'readyBlade', far: 'hang', face: 'block' },
  { feet: 1.3, f: [-7, 7], o: 9, lean: 1, tilt: 10, arm: 'readyBlade', far: 'hang', face: 'block' },
  { feet: 1.25, f: [-6, 6], o: 7, lean: 1, tilt: 8, arm: 'readyBlade', far: 'fwd' },
  { feet: 1.2, f: [-6, 5], o: 5, lean: 1, tilt: 6, arm: 'readyBlade', far: 'fwd' },
  { feet: 1.15, f: [-6, 5], o: 4, lean: 1, tilt: 5, arm: 'readyBlade', far: 'fwd' },
];

/** ฟันจากสไลด์ (slide strike): out of the drop stance the blade, drawn low behind, sweeps up through the foe as the body
 * rises off the back leg, finishing high, then settles into the stance. */
const SLIDE_STRIKE: Key[] = [
  { feet: 1.4, f: [-9, 11], o: 12, lean: 1, tilt: 8, arm: 'lowBack', far: 'back', B: [-11, 40], face: 'atk' },
  { feet: 1.4, f: [-8, 10], o: 11, lean: 1, tilt: 5, arm: 'risingSweep', far: 'back', B: [-12, 39], face: 'atk' },
  { feet: 1.35, f: [-7, 9], o: 8, lean: 1, tilt: 0, sway: -0.6, arm: 'risingSweep', far: 'back', face: 'atk' },
  { feet: 1.3, f: [-5, 8], o: 5, lean: 1, tilt: -4, sway: -1, arm: 'risingHigh', far: 'back', face: 'atk' },
  { feet: 1.25, f: [-6, 7], o: 4, lean: 1, tilt: -2, sway: -0.6, arm: 'risingHigh', far: 'fwd', face: 'atk' },
  { feet: 1.2, f: [-6, 6], o: 4, lean: 1, tilt: 3, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { feet: 1.15, f: [-6, 5], o: 4, lean: 1, tilt: 5, arm: 'readyBlade', far: 'fwd' },
];

/** สลัดหลุด (shake-off): out of the hit-stun the trunk wrenches round (擰身) — the weapon shoulder drawn back, then
 * whipped through low (an arm flung out high swept the blade across the face), breaking the hold — and the body springs into a back-step with the sword-fingers
 * up, lands sunk over the back leg and rises into the stance. */
const ESCAPE: Key[] = [
  { feet: 1.2, f: [-6, 5], o: 6, lean: 0, tilt: 10, turn: -6, headDx: -1, arm: 'back', far: 'fwd', face: 'block' },
  { feet: 1.3, f: [-8, 4], l: [2, 0], o: 4, lean: -1, tilt: -2, turn: 6, sway: -1.5, hem: 2, flare: 2, arm: 'low', far: 'back', face: 'block' },
  { feet: 1.2, f: [-10, 3], l: [4, 4], o: 1, lean: -2, tilt: -8, turn: 3, sway: -2.5, hem: 3, flare: 2, arm: 'readyBlade', far: 'up', B: FLUNG, face: 'block' },
  { feet: 1.4, f: [-11, 6], l: [0, 2], o: 6, lean: -2, tilt: -2, turn: 1, sway: -1.5, hem: 2, flare: 1, arm: 'readyBlade', far: 'hang', face: 'block' },
  { w: -0.7, feet: 1.4, f: [-9, 7], o: 8, lean: -1, tilt: 5, hipDrop: 1, shoulderDrop: 0.6, sway: -0.4, arm: 'readyBlade', far: 'fwd', face: 'block' },
  { w: -0.3, feet: 1.25, f: [-7, 6], o: 6, lean: 0, tilt: 5, arm: 'readyBlade', far: 'fwd' },
  { feet: 1.15, f: [-6, 5], o: 4, lean: 1, tilt: 5, arm: 'readyBlade', far: 'fwd' },
];

/** Either end of a move: a key pose, or the stance it starts from / ends in (ready in a fight, at ease out of one). */
type End = string;

/** The moves: their keys and what they start from and end on. Order is the motions' order (their phase). */
const MOVES = {
  qsBack: { keys: BACK, from: 'stance', to: 'stance' },
  qsSideR: { keys: SIDE, from: 'stance', to: 'stance' },
  qsSideL: { keys: SIDE.map(mirror), from: 'stance', to: 'stance' },
  qsFwd: { keys: FWD, from: 'stance', to: 'stance' },
  qsTakeoff: { keys: TAKEOFF, from: 'stance', to: 'qsAir_1' },
  qsAir: { keys: AIR, from: null, to: null },
  qsAirDash: { keys: AIR_DASH, from: 'qsAir_3', to: 'qsAir_4' },
  qsDive: { keys: DIVE, from: 'qsAir_4', to: null },
  qsDiveLand: { keys: DIVE_LAND, from: 'qsDive_7', to: 'ready' },
  qsLand: { keys: LAND, from: 'qsAir_8', to: 'stance' },
  qsSlide: { keys: SLIDE_, from: 'stance', to: null },
  qsSlideStop: { keys: SLIDE_STOP, from: 'qsSlide_7', to: 'stance' },
  qsSlideStrike: { keys: SLIDE_STRIKE, from: 'qsSlide_5', to: 'ready' },
  qsEscape: { keys: ESCAPE, from: 'hurt', to: 'stance' },
} satisfies Record<MoveId, { keys: Key[]; from: End | null; to: End | null }>;

/** Moves with an end on the stance: drawn twice, from/to the ready stance (`id`) and from/to standing at ease (`idE`). */
type EaseId = 'qsBack' | 'qsSideR' | 'qsSideL' | 'qsFwd' | 'qsTakeoff' | 'qsLand' | 'qsSlide' | 'qsSlideStop' | 'qsEscape';
type MoveId = EaseId | 'qsAir' | 'qsAirDash' | 'qsDive' | 'qsDiveLand' | 'qsSlideStrike';
export type DuelMotionId = MoveId | `${EaseId}E`;

/** This group's key poses: `<motion>_<i>`. */
export type Poses = `${MoveId}_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`;

const POSES_: Partial<Record<Poses, PoseRig<Poses>>> = {};
const MOTIONS_: { id: DuelMotionId; keys: readonly string[]; ease: readonly Ease[] }[] = [];
for (const [id, mv] of Object.entries(MOVES) as [MoveId, { keys: Key[]; from: End | null; to: End | null }][]) {
  const names: string[] = [];
  mv.keys.forEach((k, i) => {
    const name = `${id}_${i + 1}` as Poses;
    names.push(name);
    const a = Math.abs(k.w ?? 0);
    const stance = {
      label: id,
      female: false,
      keys: [name, name],
      motion: id,
      feet: k.feet,
      ...(a > 0.02 ? { weight: (k.w ?? 0) > 0 ? ('A' as const) : ('B' as const) } : {}),
      shift: a,
      shiftIn: 0,
      hipDrop: k.hipDrop ?? 0,
      shoulderDrop: k.shoulderDrop ?? 0,
      step: 0,
      turn: k.turn ?? 0,
      tilt: k.tilt,
      breath: 0,
      tuck: 1,
      rest: 'side',
    } as unknown as Stance;
    IDLE_KEYS.set(name, { stance, inhale: true, feet: k.f, ...(k.B ? { hands: { B: k.B } } : {}) });
    const rig: Partial<Rig> = {
      o: k.o,
      lean: k.lean,
      tilt: k.tilt,
      headDx: k.headDx ?? 0,
      l1: k.l?.[0] ?? 0,
      l2: k.l?.[1] ?? 0,
      sway: k.sway ?? 0,
      hem: k.hem ?? 0,
      flare: k.flare ?? 0,
      arm: k.arm,
      far: k.far,
      face: k.face ?? 'idle',
    };
    POSES_[name] = () => rig;
  });
  const keysWith = (stance: 'ready' | 'idle'): string[] => [
    ...(mv.from === null ? [] : [mv.from === 'stance' ? stance : mv.from]),
    ...names,
    ...(mv.to === null ? [] : [mv.to === 'stance' ? stance : mv.to]),
  ];
  const ease = (n: number): Ease[] => Array.from({ length: n - 1 }, () => 'inOut' as const);
  const k = keysWith('ready');
  MOTIONS_.push({ id, keys: k, ease: ease(k.length) });
  if (mv.from === 'stance' || mv.to === 'stance') {
    const e = keysWith('idle');
    MOTIONS_.push({ id: `${id}E` as DuelMotionId, keys: e, ease: ease(e.length) });
  }
}

export const POSES = POSES_ as Record<Poses, PoseRig<Poses>>;

/** The moves' motions (8–9 keys each). */
export const MOTIONS = MOTIONS_ as unknown as readonly {
  id: DuelMotionId;
  keys: readonly Pose[];
  ease: readonly Ease[];
}[];

/** Typed check that the motions fit the shared shape. */
export const _check: readonly MotionDef[] = MOTIONS;
